import type { ElementType, FC } from 'react';

import { StyledLink } from '@xing-com/crate-layout-nav-item-styled-link';
import { useActiveItem, useNotifications } from '@xing-com/hub';
import { IconWithBadge } from '@xing-com/platform-layout-icon-with-badge';
import { addParams } from '@xing-com/xing-tracking';

import * as Styled from './nav-item-header.styled';

type NavItemHeaderIconProps = {
  className?: string;
  count?: number;
};

type NavItemHeaderProps = {
  icon: ElementType<NavItemHeaderIconProps>;
  passNotificationCountToIcon?: boolean;
  activeIcon: ElementType<NavItemHeaderIconProps>;
  href: string;
  onClick?: (event: React.MouseEvent) => void;
  notificationKey?: string;
  title: string;
  activeItemValues?: string[];
  tracking?: Record<string, string>;
};

export const NavItemHeader: FC<NavItemHeaderProps> = ({
  icon,
  passNotificationCountToIcon,
  activeIcon,
  href,
  onClick,
  notificationKey,
  title,
  activeItemValues,
  tracking,
}) => {
  const activeItem = useActiveItem();
  const notifications = useNotifications();
  const url = href;

  const isActive =
    activeItem && activeItemValues
      ? activeItemValues.includes(activeItem)
      : false;
  const notificationCount =
    notifications && notifications[notificationKey || ''];

  // When active, use regular icon as fallback if no activeIcon is specified
  const BaseIcon = isActive && activeIcon ? activeIcon : icon;
  const IconElement: FC<{ className?: string }> = ({ className }) => (
    <BaseIcon
      className={className}
      count={
        passNotificationCountToIcon && BaseIcon === icon
          ? notificationCount || 0
          : undefined
      }
    />
  );

  const to = tracking ? addParams(url, tracking) : url;

  return (
    <StyledLink
      $isActive={isActive}
      data-testid={`frame-vnav-${activeItemValues?.join('_')}`}
      to={to}
      onClick={onClick}
    >
      <IconWithBadge
        icon={IconElement}
        title={title}
        count={notificationCount}
      />
      <Styled.Title>{title}</Styled.Title>
    </StyledLink>
  );
};
